import { defineProcedure, ProcedureCategory } from '../../../ontology.model';
import { models } from './representations';

export const standardFormProcedures = [
  defineProcedure(models, {
    id: 'proc.num.sf.construct',
    name: 'Construct Standard Form',
    category: ProcedureCategory.Transformation,
    input_ids: ['rep.num.sf.params'],
    output_id: 'rep.num.sf.val',
    formula_latex: 'a \\times 10^n',
    execute: (params) => {
      return {
        notation: `${params.coefficient} \\times 10^{${params.exponent}}`,
        coefficient: params.coefficient,
        exponent: params.exponent,
      };
    },
  }),

  defineProcedure(models, {
    id: 'proc.num.sf.to_sf',
    name: 'Convert to Standard Form',
    category: ProcedureCategory.Transformation,
    tag_ids: ['tag.sf.transform'],
    input_ids: ['rep.num.sf.ord'],
    output_id: 'rep.num.sf.val',
    formula_latex: 'x = a \\times 10^n',
    execute: (ord) => {
      const val = ord.value;
      if (val === 0) {
        return {
          notation: '0',
          coefficient: 0,
          exponent: 0,
        };
      }

      const exponent = Math.floor(Math.log10(Math.abs(val)));
      const coefficient = val / Math.pow(10, exponent);

      if (Math.abs(coefficient) >= 10) {
        return {
          notation: `${coefficient / 10} \\times 10^{${exponent + 1}}`,
          coefficient: coefficient / 10,
          exponent: exponent + 1,
        };
      }

      let a = coefficient;
      let n = exponent;

      if (Math.abs(a) >= 10) {
        a /= 10;
        n += 1;
      } else if (Math.abs(a) < 1) {
        a *= 10;
        n -= 1;
      }

      return {
        notation: `${a} \\times 10^{${n}}`,
        coefficient: a,
        exponent: n,
      };
    },
  }),

  defineProcedure(models, {
    id: 'proc.num.sf.to_ord',
    name: 'Convert to Ordinary Number',
    category: ProcedureCategory.Transformation,
    tag_ids: ['tag.sf.transform'],
    input_ids: ['rep.num.sf.val'],
    output_id: 'rep.num.sf.ord',
    formula_latex: 'a \\times 10^n = x',
    execute: (sf) => {
      return { value: sf.coefficient * Math.pow(10, sf.exponent) };
    },
  }),

  defineProcedure(models, {
    id: 'proc.num.sf.mul',
    name: 'Multiply Standard Form',
    category: ProcedureCategory.Operation,
    tag_ids: ['tag.sf.ops'],
    input_ids: ['rep.num.sf.val', 'rep.num.sf.val'],
    output_id: 'rep.num.sf.val',
    formula_latex: '(a \\times 10^n) \\times (b \\times 10^m) = (a \\times b) \\times 10^{n+m}',
    execute: (sf1, sf2) => {
      let newCoeff = sf1.coefficient * sf2.coefficient;
      let newExp = sf1.exponent + sf2.exponent;

      if (Math.abs(newCoeff) >= 10) {
        newCoeff /= 10;
        newExp += 1;
      }

      return {
        notation: `${newCoeff} \\times 10^{${newExp}}`,
        coefficient: newCoeff,
        exponent: newExp,
      };
    },
  }),

  defineProcedure(models, {
    id: 'proc.num.sf.div',
    name: 'Divide Standard Form',
    category: ProcedureCategory.Operation,
    tag_ids: ['tag.sf.ops'],
    input_ids: ['rep.num.sf.val', 'rep.num.sf.val'],
    output_id: 'rep.num.sf.val',
    formula_latex: '\\frac{a \\times 10^n}{b \\times 10^m} = \\frac{a}{b} \\times 10^{n-m}',
    execute: (sf1, sf2) => {
      let newCoeff = sf1.coefficient / sf2.coefficient;
      let newExp = sf1.exponent - sf2.exponent;

      if (Math.abs(newCoeff) < 1 && newCoeff !== 0) {
        newCoeff *= 10;
        newExp -= 1;
      } else if (Math.abs(newCoeff) >= 10) {
        newCoeff /= 10;
        newExp += 1;
      }

      return {
        notation: `${newCoeff} \\times 10^{${newExp}}`,
        coefficient: newCoeff,
        exponent: newExp,
      };
    },
  }),

  defineProcedure(models, {
    id: 'proc.num.sf.add',
    name: 'Add Standard Form',
    category: ProcedureCategory.Operation,
    tag_ids: ['tag.sf.ops'],
    input_ids: ['rep.num.sf.val', 'rep.num.sf.val'],
    output_id: 'rep.num.sf.val',
    formula_latex: '(a \\times 10^n) + (b \\times 10^m)',
    execute: (sf1, sf2) => {
      const maxExp = Math.max(sf1.exponent, sf2.exponent);

      const coeff1 = sf1.coefficient * Math.pow(10, sf1.exponent - maxExp);
      const coeff2 = sf2.coefficient * Math.pow(10, sf2.exponent - maxExp);

      let newCoeff = coeff1 + coeff2;
      let newExp = maxExp;

      while (Math.abs(newCoeff) >= 10) {
        newCoeff /= 10;
        newExp += 1;
      }
      while (Math.abs(newCoeff) < 1 && newCoeff !== 0) {
        newCoeff *= 10;
        newExp -= 1;
      }

      return {
        notation: `${newCoeff} \\times 10^{${newExp}}`,
        coefficient: newCoeff,
        exponent: newExp,
      };
    },
  }),

  defineProcedure(models, {
    id: 'proc.num.sf.sub',
    name: 'Subtract Standard Form',
    category: ProcedureCategory.Operation,
    tag_ids: ['tag.sf.ops'],
    input_ids: ['rep.num.sf.val', 'rep.num.sf.val'],
    output_id: 'rep.num.sf.val',
    formula_latex: '(a \\times 10^n) - (b \\times 10^m)',
    execute: (sf1, sf2) => {
      const maxExp = Math.max(sf1.exponent, sf2.exponent);

      const coeff1 = sf1.coefficient * Math.pow(10, sf1.exponent - maxExp);
      const coeff2 = sf2.coefficient * Math.pow(10, sf2.exponent - maxExp);

      let newCoeff = coeff1 - coeff2;
      let newExp = maxExp;

      while (Math.abs(newCoeff) >= 10) {
        newCoeff /= 10;
        newExp += 1;
      }
      while (Math.abs(newCoeff) < 1 && newCoeff !== 0) {
        newCoeff *= 10;
        newExp -= 1;
      }

      return {
        notation: `${newCoeff} \\times 10^{${newExp}}`,
        coefficient: newCoeff,
        exponent: newExp,
      };
    },
  }),
];
